import _cloneDeep from "lodash.clonedeep";
import { MutationFunction, useMutation, useQueryClient } from "react-query";
import { ISession, PlayoffsEntry } from "./useSession";

export interface EntryResponse {
  entry: PlayoffsEntry;
}

/**
 * Return a new sessions store given an entry response.
 */
export function updateSessionFromEntryResponse(
  entryRes: EntryResponse,
  session: ISession,
): ISession {
  const newSession = _cloneDeep(session);
  const newEntry = entryRes.entry;

  if (!newEntry) {
    throw new Error(`API Error: could not create new entry: ${newEntry}`);
  }

  if (!newSession.entries.chase) {
    newSession.entries.chase = [];
  }

  // replace the entry if it exists
  let hasEntry = false;
  newSession.entries.chase = newSession.entries.chase.map((entry) => {
    if (entry.id === newEntry.id) {
      hasEntry = true;
      return newEntry;
    }
    return entry;
  });

  // append entry if it doesn't exist
  if (!hasEntry) {
    newSession.entries.chase.push(newEntry);
  }

  // ensure entryCount on the session remains accurate
  newSession.user.chase_entries_count = newSession.entries.chase.length;

  return newSession;
}

/**
 * Updates the session store using an EntryResponse.
 * @entryFn Any function that returns a promise & resolves EntryResponse data.
 */
export default function useEntryMutation(
  entryFn: MutationFunction<EntryResponse>,
  options?: {
    onMutate?: (variables: any) => void;
    onSuccess?: (data: any) => void;
    onError?: (
      error: unknown,
      variables: unknown,
      context: {
        prevSession: ISession;
      },
    ) => void;
  },
) {
  const queryClient = useQueryClient();

  return useMutation(entryFn, {
    onMutate: (variables) => {
      // Snapshot the previous value
      const prevSession: ISession = queryClient.getQueryData("session");
      // console.log(`prevSession snapshot`, prevSession?.entries?.chase?.[0]?.picks?.[2]);

      options?.onMutate?.(variables); //* Optionally pass an onMutate function that changes the queryClient based on your variables.
      // Return a context object with the snapshot value
      return { prevSession };
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
      queryClient.setQueryData<ISession>("session", (prevSession) =>
        updateSessionFromEntryResponse(data, prevSession),
      );
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, data, context: { prevSession: ISession }) => {
      // console.log(`context`, context.prevSession?.entries?.chase?.[0]?.picks?.[2]);
      options?.onError?.(err, data, context);
      queryClient.setQueryData("session", context.prevSession);
    },
  });
}
